import * as React from "react";
import type { Omittable } from "../../lib/props";

export type ErrorInfo = { componentStack: string };

export type ErrorBoundaryFallback =
  | React.ReactNode
  | ((props: { error: Error; reset: () => void }) => React.ReactNode);

export type ErrorBoundaryProps = Omittable<{
  /**
   * Called once per caught render error. Consumers typically forward this
   * to a logger or error reporter. Keeping the library decoupled from any
   * reporter keeps the primitive portable.
   */
  onError?: (error: Error, info: ErrorInfo) => void;
  /**
   * Render prop or node to show when an error has been caught. Render-prop
   * form receives `{ error, reset }`; the node form is static.
   */
  fallback?: ErrorBoundaryFallback;
  /**
   * When this value changes, the boundary auto-resets. Pass the current
   * route path to clear the error on navigation.
   */
  resetKey?: unknown;
  children: React.ReactNode;
}>;

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * React error boundary primitive. Catches synchronous render/lifecycle
 * errors in its subtree and renders `fallback`. Does NOT catch async errors
 * (set-state in a Promise handler) or event-handler throws — those need global
 * `window.error` / `unhandledrejection` listeners.
 */
export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.props.onError?.(error, {
      componentStack: info.componentStack ?? "",
    });
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const { fallback } = this.props;
    if (typeof fallback === "function") {
      return fallback({ error, reset: this.reset });
    }
    return fallback ?? null;
  }
}
